/**
 * Bilingual copy and imagery for the Voices of Impact NY event page
 * (pages/VoicesOfImpactNYPage.tsx). Kept out of the component so the page
 * file stays focused on layout.
 */

export const VOI_IMAGES = {
  sandoval:
    'https://cdn.magicpatterns.com/uploads/41ina9BJwrzGYJTD1injLe/NeidaS.jpg',
  zuniga:
    'https://cdn.magicpatterns.com/uploads/sCwWAyRQF74f8hLN1q2Rrm/JoseZ.jpg',
  alvarez:
    'https://cdn.magicpatterns.com/uploads/bmHrNj6MWDZD2JVFmaG48J/Copan_Alvarez.jpg',
  flyer:
    'https://cdn.magicpatterns.com/uploads/fdgKGqY2axfX3n3ihCbwUD/Voices_of_Impact_-_New_York.jpg',
  backdrop:
    'https://cdn.magicpatterns.com/patterns/generated-images/6004ba36-767f-4fce-adb9-f35b03fce46c.jpg',
} as const

/** Film and television credits woven into José Zuñiga's bio. */
export const ZUNIGA_FILMS = [
  'Crooklyn',
  'Smoke',
  'Con Air',
  'Alien Resurrection',
  'Constantine',
]
export const ZUNIGA_SHOWS = ['Law & Order', 'NYPD Blue', 'ER', '24']

export const VOI_COPY = {
  EN: {
    back: 'Back to site',
    eyebrow: 'Hispanic Heritage Month · New York City',
    subtitle: 'A Hispanic Heritage Month Celebration',
    details: [
      { label: 'Date', value: 'Thursday, October 1' },
      { label: 'Time', value: '5:00 – 8:00 PM' },
      { label: 'Venue', value: '30 Rockefeller Plaza, New York, NY' },
      { label: 'Format', value: 'Evening reception' },
    ],
    flyerAria: 'View the full invitation graphic',
    flyerAlt:
      'Voices of Impact — A Hispanic Heritage Month Celebration, Thursday, October 1, 30 Rockefeller Plaza, New York, 5–8 PM',
    rsvpTitle: 'Invited Guest RSVP',
    rsvpText:
      'Guests who have received a formal invitation may confirm their attendance below.',
    guestsEyebrow: 'Special Guests',
    sandovalAlt: 'Voices of Impact special guest Neida Sandoval',
    zunigaAlt: 'Voices of Impact special guest José Zuñiga',
    alvarezAlt: 'Voices of Impact special guest Copán Álvarez',
    guestsHeading: 'Joining Us That Evening',
    guestsIntro:
      "We are honored to welcome three distinguished Latino voices whose work in journalism, the arts, and business leadership has carried our community's story to national audiences.",
    alvarezRole: 'Journalist & Business Leader',
    alvarezBio:
      'A world-renowned sports journalist, Copán Álvarez has covered the biggest stages in international sport — World Cups, Olympic Games, and championship fights — bringing the passion of Latin American athletics to audiences across the Americas. Beyond the broadcast booth, he has used his platform to open doors for Latino professionals in an industry where they have long been underrepresented, and to champion the next generation of storytellers.',
    sandovalRole: 'Journalist & Broadcast Anchor',
    sandovalBio:
      'With more than three decades in Spanish-language television, Neida Sandoval became one of the most recognized faces in U.S. Hispanic media as a longtime national morning-show anchor. Her reporting has taken her from natural disasters and immigration stories to interviews with heads of state, earning her Emmy recognition and a reputation for bringing Latino stories to audiences that rarely heard them.',
    zunigaRole: 'Actor, Film & Television',
    zunigaBioLead:
      'A Latino actor who has built a decades-long career across film, television, and the New York stage, Jose Zuniga has appeared in ',
    zunigaBioMid: ', with television credits spanning ',
    zunigaBioEnd:
      '. His body of work has helped broaden how Latino characters are written and portrayed on screen.',
    aboutEyebrow: 'About the Evening',
    aboutHeading: 'A Room You Will Want to Be In',
    aboutParagraphs: [
      'Designed to create meaningful connections across industries and generations, Voices of Impact offers guests the opportunity to expand their networks, exchange ideas, discover new opportunities, and build relationships with individuals who are making an impact in their communities and beyond.',
      'Throughout the evening, guests will also hear from distinguished special guests and inspiring voices whose leadership reflects the growing influence and contributions of the Latino community in the United States.',
      'Whether you are looking to meet fellow leaders, develop new partnerships, connect with emerging talent, or simply become part of a growing national network, Voices of Impact is a room you will want to be in.',
    ],
    highlights: [
      {
        title: 'A room built for connection',
        text: 'Leaders, executives, entrepreneurs, professionals, public servants, cultural figures, and changemakers from across the country, gathered in one place.',
      },
      {
        title: 'Voices worth hearing',
        text: 'Distinguished special guests and inspiring speakers whose leadership reflects the growing influence of the Latino community across the country.',
      },
      {
        title: 'Across industries and generations',
        text: 'Expand your network, exchange ideas, discover opportunities, and meet emerging talent alongside established leaders.',
      },
    ],
    missionHeading: 'Connect. Empower. Celebrate.',
    missionText:
      'At the Honduran-American Association, our mission is to Connect, Empower, and Celebrate. This evening is about putting that mission into action — bringing remarkable people together and creating connections that can lead to new ideas, new collaborations, and lasting impact.',
    missionClosing:
      'Come ready to connect. Leave with new relationships, new possibilities, and a stronger sense of what we can build together.',
    footer: 'Hosted by the Honduran-American Association',
  },
  ES: {
    back: 'Volver al sitio',
    eyebrow: 'Mes de la Herencia Hispana · Ciudad de Nueva York',
    subtitle: 'Una Celebración del Mes de la Herencia Hispana',
    details: [
      { label: 'Fecha', value: 'Jueves, 1 de octubre' },
      { label: 'Hora', value: '5:00 – 8:00 P.M.' },
      { label: 'Lugar', value: '30 Rockefeller Plaza, Nueva York, NY' },
      { label: 'Formato', value: 'Recepción vespertina' },
    ],
    flyerAria: 'Ver la invitación completa',
    flyerAlt:
      'Voices of Impact — Una Celebración del Mes de la Herencia Hispana, jueves 1 de octubre, 30 Rockefeller Plaza, Nueva York, 5–8 P.M.',
    rsvpTitle: 'Confirmación de Invitados',
    rsvpText:
      'Los invitados que han recibido una invitación formal pueden confirmar su asistencia a continuación.',
    guestsEyebrow: 'Invitados Especiales',
    sandovalAlt: 'Invitada especial de Voices of Impact, Neida Sandoval',
    zunigaAlt: 'Invitado especial de Voices of Impact, José Zuñiga',
    alvarezAlt: 'Invitado especial de Voices of Impact, Copán Álvarez',
    guestsHeading: 'Nos Acompañan Esa Noche',
    guestsIntro:
      'Nos honra recibir a tres distinguidas voces latinas cuyo trabajo en el periodismo, las artes y el liderazgo empresarial ha llevado la historia de nuestra comunidad a audiencias nacionales.',
    alvarezRole: 'Periodista y Líder Empresarial',
    alvarezBio:
      'Periodista deportivo de renombre mundial, Copán Álvarez ha cubierto los escenarios más importantes del deporte internacional — Copas del Mundo, Juegos Olímpicos y peleas de campeonato — llevando la pasión del deporte latinoamericano a audiencias de toda América. Más allá de la cabina de transmisión, ha usado su plataforma para abrir puertas a profesionales latinos en una industria donde han estado subrepresentados durante mucho tiempo, y para impulsar a la próxima generación de narradores.',
    sandovalRole: 'Periodista y Presentadora',
    sandovalBio:
      'Con más de tres décadas en la televisión en español, Neida Sandoval se convirtió en uno de los rostros más reconocidos de los medios hispanos en Estados Unidos como presentadora nacional de programas matutinos. Su labor periodística la ha llevado desde desastres naturales e historias de inmigración hasta entrevistas con jefes de Estado, obteniendo reconocimiento Emmy y una reputación por llevar las historias latinas a audiencias que rara vez las escuchaban.',
    zunigaRole: 'Actor de Cine y Televisión',
    zunigaBioLead:
      'Actor latino con una carrera de décadas en el cine, la televisión y los escenarios de Nueva York, Jose Zuniga ha aparecido en ',
    zunigaBioMid: ', con créditos televisivos que incluyen ',
    zunigaBioEnd:
      '. Su trayectoria ha ayudado a ampliar la forma en que los personajes latinos se escriben y se representan en pantalla.',
    aboutEyebrow: 'Sobre la Velada',
    aboutHeading: 'Una Sala en la Que Querrá Estar',
    aboutParagraphs: [
      'Diseñada para crear conexiones significativas entre industrias y generaciones, Voices of Impact ofrece a los invitados la oportunidad de ampliar sus redes, intercambiar ideas, descubrir nuevas oportunidades y construir relaciones con personas que están generando impacto en sus comunidades y más allá.',
      'A lo largo de la velada, los invitados también escucharán a distinguidos invitados especiales y voces inspiradoras cuyo liderazgo refleja la creciente influencia y las contribuciones de la comunidad latina en los Estados Unidos.',
      'Ya sea que busque conocer a otros líderes, desarrollar nuevas alianzas, conectar con talento emergente o simplemente formar parte de una red nacional en crecimiento, Voices of Impact es una sala en la que querrá estar.',
    ],
    highlights: [
      {
        title: 'Una sala hecha para conectar',
        text: 'Líderes, ejecutivos, empresarios, profesionales, servidores públicos, figuras culturales y agentes de cambio de todo el país, reunidos en un mismo lugar.',
      },
      {
        title: 'Voces que vale la pena escuchar',
        text: 'Invitados especiales distinguidos y oradores inspiradores cuyo liderazgo refleja la creciente influencia de la comunidad latina en todo el país.',
      },
      {
        title: 'Entre industrias y generaciones',
        text: 'Amplíe su red, intercambie ideas, descubra oportunidades y conozca talento emergente junto a líderes consolidados.',
      },
    ],
    missionHeading: 'Conectar. Empoderar. Celebrar.',
    missionText:
      'En la Asociación Hondureño-Americana, nuestra misión es Conectar, Empoderar y Celebrar. Esta velada es esa misión en acción: reunir a personas extraordinarias y crear vínculos que puedan dar paso a nuevas ideas, nuevas colaboraciones y un impacto duradero.',
    missionClosing:
      'Venga listo para conectar. Váyase con nuevas relaciones, nuevas posibilidades y una idea más clara de lo que podemos construir juntos.',
    footer: 'Organizado por la Asociación Hondureño-Americana',
  },
}
